import {
  Dispatch,
  useCallback,
  useEffect,
  useRef,
  useState,
  SetStateAction
} from "react";
import { useSelector } from "react-redux";
import { ErrorMessage } from "@tvg/wallet/src/types";
import { getAccountNumber } from "@urp/store-selectors";
import { createBraintreeClient } from "../utils/braintreeClient";
import useIsBraintreeEnabled from "./useIsBraintreeEnabled";
import {
  BRAINTREE_ERROR_CODES,
  BRAINTREE_SDK_SCRIPT_LOAD_TIMEOUT_MS,
  BRAINTREE_SDK_SCRIPT_POLL_INTERVAL_MS
} from "../constants";
import { fetchAndValidateClientToken } from "./useBraintreeHostedFields";
import {
  getCachedClientToken,
  invalidateCachedClientToken
} from "../utils/braintreeClientTokenCache";
import type {
  BraintreeClientInstance,
  BraintreeDataCollectorInstance,
  BraintreeHostedFieldsInstance,
  HostedFieldEvent,
  HostedFieldState
} from "../types/braintree";

export interface CvvFieldState {
  cvv: HostedFieldState;
}

interface UseBraintreeCvvHostedFieldProps {
  isActive: boolean;
  setErrorMessage: Dispatch<SetStateAction<ErrorMessage>>;
}

const DEFAULT_CVV_FIELD_STATE: HostedFieldState = {
  container: null,
  isFocused: false,
  isEmpty: true,
  isPotentiallyValid: true,
  isValid: false
};

/**
 * Lightweight hook that initialises a CVV-only Braintree Hosted Field plus a
 * Data Collector. Use for vault deposits where the user must re-enter their CVV
 * (PCI SAQ A compliant via Braintree iframe).
 *
 * Returns:
 *  - isReady    – hosted field iframe is mounted and ready
 *  - isLoading  – initialisation in progress
 *  - cvvFieldState – current validity/focus state of the CVV field
 *  - deviceData – fraud-screening device data from the data collector
 *  - tokenizeCvv – async function that calls Braintree's tokenize({ fieldsToTokenize: ['cvv'] })
 *  - teardown   – imperative teardown (called automatically on unmount / isActive=false)
 */
export const useBraintreeCvvHostedField = ({
  isActive,
  setErrorMessage
}: UseBraintreeCvvHostedFieldProps) => {
  const accountId = useSelector(getAccountNumber) as string;
  const { isEnabled: isBraintreeCCEnabled, isResolved: isBraintreeResolved } =
    useIsBraintreeEnabled();

  const [isReady, setIsReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [cvvFieldState, setCvvFieldState] = useState<CvvFieldState>({
    cvv: { ...DEFAULT_CVV_FIELD_STATE }
  });
  const [deviceData, setDeviceData] = useState("");
  const [isSdkAvailable, setIsSdkAvailable] = useState(
    typeof window !== "undefined" &&
      !!window.braintree?.hostedFields &&
      !!window.braintree?.dataCollector
  );

  const [isContainerReady, setIsContainerReady] = useState(false);
  const containerRef = useCallback((node: HTMLElement | null) => {
    setIsContainerReady(!!node);
  }, []);

  const hostedFieldsRef = useRef<BraintreeHostedFieldsInstance | null>(null);
  const clientRef = useRef<BraintreeClientInstance | null>(null);
  const collectorRef = useRef<BraintreeDataCollectorInstance | null>(null);
  const isInitializingRef = useRef(false);
  const teardownPromiseRef = useRef<Promise<void> | null>(null);

  // Poll for the Braintree SDK scripts (mirrors useBraintreeHostedFields pattern)
  useEffect(() => {
    if (
      isSdkAvailable ||
      !isActive ||
      !isBraintreeResolved ||
      !isBraintreeCCEnabled ||
      typeof window === "undefined"
    ) {
      return undefined;
    }

    const intervalId = setInterval(() => {
      if (window.braintree?.hostedFields && window.braintree?.dataCollector) {
        clearTimeout(timeoutId);
        setIsSdkAvailable(true);
        clearInterval(intervalId);
      }
    }, BRAINTREE_SDK_SCRIPT_POLL_INTERVAL_MS);

    const timeoutId = setTimeout(() => {
      clearInterval(intervalId);
      setErrorMessage((prevState) => ({
        ...prevState,
        isOpen: true,
        errorCode: BRAINTREE_ERROR_CODES.INIT_ERROR,
        isRetry: false
      }));
    }, BRAINTREE_SDK_SCRIPT_LOAD_TIMEOUT_MS);

    return () => {
      clearInterval(intervalId);
      clearTimeout(timeoutId);
    };
  }, [
    isSdkAvailable,
    isActive,
    isBraintreeResolved,
    isBraintreeCCEnabled,
    setErrorMessage
  ]);

  const teardown = useCallback(() => {
    const promises: Promise<void>[] = [];

    if (hostedFieldsRef.current) {
      promises.push(hostedFieldsRef.current.teardown().catch(() => {}));
      hostedFieldsRef.current = null;
    }
    if (collectorRef.current) {
      promises.push(collectorRef.current.teardown().catch(() => {}));
      collectorRef.current = null;
    }
    if (clientRef.current) {
      promises.push(clientRef.current.teardown().catch(() => {}));
      clientRef.current = null;
    }

    teardownPromiseRef.current =
      promises.length > 0 ? Promise.all(promises).then(() => {}) : null;

    setIsReady(false);
    setIsLoading(false);
    setDeviceData("");
    setCvvFieldState({ cvv: { ...DEFAULT_CVV_FIELD_STATE } });
    isInitializingRef.current = false;
  }, []);

  // Tear down when deactivated or unmounted
  useEffect(() => {
    if (!isActive || !isBraintreeCCEnabled) {
      teardown();
      isInitializingRef.current = false;
    }
  }, [isActive, isBraintreeCCEnabled, teardown]);

  useEffect(() => () => teardown(), [teardown]);

  // Initialise the hosted field + data collector
  useEffect(() => {
    if (
      !isBraintreeCCEnabled ||
      !isActive ||
      !accountId ||
      isReady ||
      isInitializingRef.current ||
      !isSdkAvailable ||
      !isContainerReady
    ) {
      return undefined;
    }

    const abortController = new AbortController();

    const tryInit = async (forceRefresh: boolean): Promise<void> => {
      const tokenData = await fetchAndValidateClientToken(
        accountId,
        setErrorMessage,
        abortController.signal,
        forceRefresh
      );
      if (!tokenData || abortController.signal.aborted) return;

      const clientInstance = await createBraintreeClient(tokenData.token);
      if (abortController.signal.aborted) {
        clientInstance.teardown().catch(() => {});
        return;
      }
      clientRef.current = clientInstance;

      const hostedFieldsInstance = await window.braintree!.hostedFields!.create(
        {
          client: clientInstance,
          fields: {
            cvv: {
              container: "#braintree-vault-cvv",
              placeholder: "3 digits"
            }
          }
        }
      );
      if (abortController.signal.aborted) {
        hostedFieldsInstance.teardown().catch(() => {});
        clientInstance.teardown().catch(() => {});
        clientRef.current = null;
        return;
      }
      hostedFieldsRef.current = hostedFieldsInstance;

      const updateCvvState = (event: HostedFieldEvent) => {
        const { cvv } = event.fields;
        if (cvv) {
          setCvvFieldState({ cvv });
        }
      };

      hostedFieldsInstance.on("validityChange", updateCvvState);
      hostedFieldsInstance.on("empty", updateCvvState);
      hostedFieldsInstance.on("notEmpty", updateCvvState);
      hostedFieldsInstance.on("focus", updateCvvState);
      hostedFieldsInstance.on("blur", updateCvvState);

      if (!window.braintree?.dataCollector) {
        throw new Error("Braintree Data Collector SDK not loaded");
      }

      const collector = await window.braintree.dataCollector.create({
        client: clientInstance
      });
      if (abortController.signal.aborted) {
        collector.teardown().catch(() => {});
        return;
      }
      collectorRef.current = collector;
      setDeviceData(collector.deviceData);

      setErrorMessage((prevState) => ({
        ...prevState,
        isOpen: false,
        errorCode: ""
      }));
      setIsLoading(false);
      setIsReady(true);
    };

    const init = async () => {
      isInitializingRef.current = true;
      setIsLoading(true);

      try {
        if (teardownPromiseRef.current) {
          await teardownPromiseRef.current;
          teardownPromiseRef.current = null;
        }
        if (abortController.signal.aborted) return;

        const usedCachedToken = !!getCachedClientToken(accountId);

        try {
          await tryInit(false);
        } catch (err) {
          if (abortController.signal.aborted || !usedCachedToken) throw err;
          // Cached token likely stale — clear, teardown partial init, retry.
          invalidateCachedClientToken();
          teardown();
          if (teardownPromiseRef.current) {
            await teardownPromiseRef.current;
            teardownPromiseRef.current = null;
          }
          if (abortController.signal.aborted) return;
          isInitializingRef.current = true;
          setIsLoading(true);
          await tryInit(true);
        }
      } catch {
        if (abortController.signal.aborted) return;
        teardown();
        setErrorMessage((prevState) => ({
          ...prevState,
          isOpen: true,
          errorCode: BRAINTREE_ERROR_CODES.INIT_ERROR,
          isRetry: false
        }));
      } finally {
        isInitializingRef.current = false;
        if (!abortController.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    init();

    return () => {
      abortController.abort();
    };
  }, [
    isBraintreeCCEnabled,
    isActive,
    isReady,
    isSdkAvailable,
    isContainerReady,
    accountId,
    setErrorMessage,
    teardown
  ]);

  const tokenizeCvv = useCallback(async (): Promise<string> => {
    if (!hostedFieldsRef.current) {
      throw new Error("CVV Hosted Field not initialized");
    }
    const payload = await hostedFieldsRef.current.tokenize({
      fieldsToTokenize: ["cvv"]
    });
    return payload.nonce;
  }, []);

  return {
    isReady,
    isLoading,
    cvvFieldState,
    deviceData,
    tokenizeCvv,
    teardown,
    containerRef
  };
};
